'use client';

import { Drawer as HeroDrawer } from '@heroui/react';
import { ReactNode } from 'react';

import { Container } from './Container';
import { HStack } from './Stack';
import { Text } from './Text';

export interface DrawerProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  body: ReactNode | ((props: { close: () => void }) => ReactNode);
  title?: ReactNode;
  footer?: ReactNode;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  backdrop?: 'transparent' | 'opaque' | 'blur';
}

const SIZE_MAP = {
  xs: 'w-64 max-w-(--spacing-64)',
  sm: 'w-80 max-w-(--spacing-80)',
  md: 'w-96 max-w-(--spacing-96)',
  lg: 'w-[32rem] max-w-(--spacing-128)',
  xl: 'w-[40rem] max-w-(--spacing-160)',
  full: 'w-screen max-w-full',
};

export const Drawer = ({
  isOpen,
  onOpenChange,
  body,
  title,
  footer,
  placement = 'right',
  size = 'md',
  backdrop = 'blur',
}: DrawerProps) => {
  const sizeClass = SIZE_MAP[size];

  return (
    <HeroDrawer>
      <HeroDrawer.Backdrop
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        variant={backdrop}
      >
        <HeroDrawer.Content placement={placement}>
          <HeroDrawer.Dialog className={sizeClass}>
            {(renderProps) => (
              <>
                <HeroDrawer.CloseTrigger />
                {title && (
                  <HeroDrawer.Header>
                    {typeof title === 'string' ? (
                      <Text variant="h3" weight="black">
                        {title}
                      </Text>
                    ) : (
                      title
                    )}
                  </HeroDrawer.Header>
                )}
                <HeroDrawer.Body>
                  <Container px={4} py={2}>
                    {typeof body === 'function' ? body(renderProps) : body}
                  </Container>
                </HeroDrawer.Body>
                {footer && (
                  <HeroDrawer.Footer>
                    <HStack padding={4} align="center" justify="end" gap={2}>
                      {footer}
                    </HStack>
                  </HeroDrawer.Footer>
                )}
              </>
            )}
          </HeroDrawer.Dialog>
        </HeroDrawer.Content>
      </HeroDrawer.Backdrop>
    </HeroDrawer>
  );
};
